import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Heading from '../Heading';
import { FlexCol, FlexRow } from '../FlexGrid';
import { Spacer } from '../LayoutPrimitives';
import ImageWithFallback from '../ImageWithFallback'

export default function DividerPromo({
    backgroundImagePath,
    height,
    className,
    heading,
    copy,
    contentContainerRight,
    overlayImagePath,
    isContained,
    contentCopyAlignRight,
    overlayImageAppearance,
    topSpacerSize,
    bottomSpacerSize,
    altOverlayImageSizes,
    overlayImageAlt,
    overlayFallbackExtension,
    children
}) {

    const heightClass = cx({
        'size-sm': height === 'sm',
        'size-md': height === 'md',
        'size-lg': height === 'lg'
    });

    const overlayPositionClass = cx({
        'overlay-image-extends-top-bottom': overlayImageAppearance === 'imageExtendsTopBottom',
        'overlay-image-set-bottom': overlayImageAppearance === 'imageSetBottom'
    });

    const setOrder = contentContainerRight ? "last" : "first";
    const setRightAlignment = contentCopyAlignRight ? 'align-content-right' : ''
    const setContainer = isContained ? 'sarsa-divider-promo-is-contained' : '';

    // Set the direction of the gradient based off the order of the container
    const gradientDirection = setOrder === "last" ? 'gradient-right-to-left' : 'gradient-left-to-right';

    // Change the direction of the gradient if the promo has an overlay image
    const altGradientDirection = overlayImagePath ? 'has-image-overlay' : '';

    return (
        <React.Fragment>
            {topSpacerSize && (
                <Spacer size={topSpacerSize} />
                )
            }
            <div
                className={`sarsa-divider-promo ${heightClass} ${overlayPositionClass} ${className}`}
                style={{
                    backgroundImage: `url(${backgroundImagePath})`
                }}>

                <div className={`sarsa-divider-promo-overlay ${gradientDirection} ${altGradientDirection}`} />

                <FlexRow className={`sarsa-divider-promo-body ${setContainer}`}>

                    <FlexCol md={6} alignSelf="center" mdOrder={setOrder} className={`sarsa-divider-promo-content-wrap ${setRightAlignment}`}>
                        <Heading headingLevel={4} appearance="h3" className="mb-1 mb-md-2">
                            {heading}
                        </Heading>
                        <p>
                            {copy}
                        </p>

                        {children}
                    </FlexCol>

                    <FlexCol>
                        {overlayImagePath && (
                            <div className={`sarsa-divider-promo-overlay-image-wrap ${heightClass}`}>
                                <ImageWithFallback
                                    imagePath={`${overlayImagePath}`}
                                    fallbackExtension={overlayFallbackExtension}
                                    alt={overlayImageAlt}
                                    altImageSizes={altOverlayImageSizes} />
                            </div>
                            )
                        }
                    </FlexCol>
                </FlexRow>
            </div>
            {bottomSpacerSize && (
                <Spacer size={bottomSpacerSize} />
                )
            }
        </React.Fragment>
    );
}


DividerPromo.propTypes = {

    /** Added to existing classes */
    className: PropTypes.string,

    /** Define the URL of the background image for the promo */
    backgroundImagePath: PropTypes.string,

    /** Set the height of the promo (sm=250px | md=300px | lg=350px) */
    height: PropTypes.oneOf(["sm", "md", "lg"]),

    /** Heading for the promo */
    heading: PropTypes.string,

    /** Copy for the promo */
    copy: PropTypes.string,

    /** Align the main content container to the right */
    contentContainerRight: PropTypes.bool,

    /**  Contain the body of the promo within the global max-width container of 1366px */
    isContained: PropTypes.bool,

    /**  Align the copy within the Content Container to the right */
    contentCopyAlignRight: PropTypes.bool,

    /**  Define the path for the overlay image */
    overlayImagePath: PropTypes.string,

    /**  Define the extension of the overlay fallback image (.jpg, .png) */
    overlayFallbackExtension: PropTypes.string,

    /**  Set the position of the overlay image within the promo */
    overlayImageAppearance: PropTypes.oneOf(['', 'imageExtendsTopBottom', 'imageSetBottom']),

    /**  Alt text for the overlay image */
    overlayImageAlt: PropTypes.string,

    /**  Display smaller file size overlay images at specific viewport breakpoints  */
    altOverlayImageSizes: PropTypes.arrayOf(
        PropTypes.shape({
            viewportMaxWidth: PropTypes.number,
            path: PropTypes.string
        })
    ),

    /**  Add a Spacer to the top of the promo. Useful if the overlay image extends outside of the promo */
    topSpacerSize: PropTypes.string,

    /**  Add a Spacer to the bottom of the promo. Useful if the overlay image extends outside of the promo */
    bottomSpacerSize: PropTypes.string,

    /** Add additional content to the component if necessary. */
    children: PropTypes.node
};

DividerPromo.defaultProps = {
    backgroundImagePath: '',
    height: 'sm',
    contentContainerRight: false,
    isContained: true,
    contentCopyAlignRight: false,
    overlayImagePath: '',
    overlayImageAppearance: '',
    topSpacerSize: '',
    bottomSpacerSize: '',
    altOverlayImageSizes: null,
    overlayImageAlt: '',
    overlayFallbackExtension: 'png'
};